// 共用遊戲介面動效：中斷後從當前狀態接續，物件銷毀時清理，遵循系統減少動態設定。
// === 使用圖片 ===
// 無 — 使用 Phaser Tween、Graphics，既有元件的圖片維持原樣。
import type Phaser from 'phaser';

const SPARK_COUNT = 12; // 總結算少量金粉
const SPARK_RADIUS = 3; // 金粉大小
const SPARK_DISTANCE = 90; // 金粉擴散距離
const SPARK_MS = 600; // 慶祝後恢復安靜
const SWEEP_WIDTH = 24; // 排名掃光寬
const SWEEP_ALPHA = 0.22; // 排名掃光亮度
const SWEEP_MS = 400; // 第一名一次掃光

export const Motion = {
  pressScale: 0.97,
  pressMs: 120,
  enterMs: 200,
  exitMs: 140,
  selectMs: 160,
  switchMs: 140,
  pageMs: 200,
  returnMs: 180,
  popMs: 220,
  popScale: 1.04,
  revealOffset: 8,
  modalScale: 0.96,
  gold: 0xffe4a1,
  glowWidth: 2,
  glowAlpha: 0.8,
  glowRadius: 12,
  glowMs: 180,
  ease: 'Cubic.easeOut',
} as const;

export interface IMotionTarget extends Phaser.GameObjects.GameObject {
  x: number;
  y: number;
  alpha: number;
  scaleX: number;
  scaleY: number;
}
export interface IMotionValues {
  x?: number;
  y?: number;
  alpha?: number;
  scaleX?: number;
  scaleY?: number;
}

const running = new WeakMap<Phaser.GameObjects.GameObject, () => void>();
const finishers = new Set<() => void>();
const media =
  typeof window !== 'undefined'
    ? window.matchMedia?.('(prefers-reduced-motion: reduce)')
    : undefined;
media?.addEventListener('change', () => {
  if (media.matches) for (const finish of [...finishers]) if (finishers.has(finish)) finish();
});
export const reducedMotion = (): boolean => media?.matches ?? false;

/** 僅中斷本模組建立的 tween，不移除遊戲本來的動畫或計時。 */
export function animateTo(
  scene: Phaser.Scene,
  target: IMotionTarget,
  values: IMotionValues,
  duration: number = Motion.enterMs,
  ease: string = Motion.ease,
  complete?: () => void,
  delay = 0
): void {
  running.get(target)?.();
  if (reducedMotion() || (duration === 0 && delay === 0)) {
    Object.assign(target, values);
    complete?.();
    return;
  }
  const cleanup = (): void => {
    running.delete(target);
    finishers.delete(finish);
    target.off('destroy', cancel);
    scene.events.off('shutdown', cancel);
  };
  const cancel = (): void => {
    cleanup();
    tween.stop();
  };
  const finish = (): void => {
    cancel();
    Object.assign(target, values);
    complete?.();
  };
  const tween = scene.tweens.add({
    targets: target,
    ...values,
    duration,
    ease,
    delay,
    onComplete: () => {
      cleanup();
      complete?.();
    },
    onStop: cleanup,
  });
  running.set(target, cancel);
  finishers.add(finish);
  target.once('destroy', cancel);
  scene.events.once('shutdown', cancel);
}

export function reveal(
  scene: Phaser.Scene,
  target: IMotionTarget,
  delay = 0,
  duration: number = Motion.enterMs,
  offset: number = Motion.revealOffset
): void {
  if (reducedMotion()) return;
  const y = target.y;
  const alpha = target.alpha;
  target.y += offset;
  target.alpha = 0;
  animateTo(scene, target, { y, alpha }, duration, Motion.ease, undefined, delay);
}

export function pop(
  scene: Phaser.Scene,
  target: IMotionTarget,
  duration: number = Motion.popMs
): void {
  if (reducedMotion()) return;
  // 保存原始比例，連續觸發不會累加放大。
  const base = target.getData('motion-pop-base') as { x: number; y: number } | undefined;
  const scale = base ?? { x: target.scaleX, y: target.scaleY };
  target.setData('motion-pop-base', scale);
  target.scaleX = scale.x * Motion.popScale;
  target.scaleY = scale.y * Motion.popScale;
  animateTo(scene, target, { scaleX: scale.x, scaleY: scale.y }, duration);
}

export function glow(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  duration: number = Motion.glowMs
): Phaser.GameObjects.Graphics | undefined {
  if (reducedMotion()) return;
  const frame = scene.add
    .graphics({ x, y })
    .lineStyle(Motion.glowWidth, Motion.gold, Motion.glowAlpha);
  frame.strokeRoundedRect(-width / 2, -height / 2, width, height, Motion.glowRadius);
  animateTo(scene, frame, { alpha: 0 }, duration, Motion.ease, () => frame.destroy());
  return frame;
}

/** Scene.start 前的短轉場；呼叫端先設離場旗標，防止重複儲存或重複切場。 */
export function leaveScene(scene: Phaser.Scene, next: () => void): void {
  if (reducedMotion()) {
    next();
    return;
  }
  scene.cameras.main.once('camerafadeoutcomplete', next);
  scene.cameras.main.fadeOut(Motion.pageMs);
}

/** 重組成中央面板與全屏遮罩，面板縮放不會露出遮罩邊緣。 */
export function showDialog(
  scene: Phaser.Scene,
  root: Phaser.GameObjects.Container,
  shade: Phaser.GameObjects.GameObject,
  cx = 0,
  cy = 0
): void {
  let body = root.getData('motion-body') as Phaser.GameObjects.Container | undefined;
  const initialize = !body || !root.visible;
  if (!body) {
    const children = root.list.filter((child) => child !== shade) as IMotionTarget[];
    body = scene.add.container(cx, cy);
    for (const child of children) {
      child.x -= cx;
      child.y -= cy;
    }
    body.add(children);
    root.add(body);
    root.setData('motion-body', body);
  }
  root.setData('motion-open', true).setVisible(true);
  const disabled = (root.getData('motion-disabled-inputs') ??
    []) as Phaser.GameObjects.GameObject[];
  for (const child of disabled) if (child.scene && child.input) child.input.enabled = true;
  root.setData('motion-disabled-inputs', []);
  const backdrop = shade as IMotionTarget;
  if (initialize) {
    body.setScale(reducedMotion() ? 1 : Motion.modalScale).setAlpha(reducedMotion() ? 1 : 0);
    backdrop.alpha = reducedMotion() ? 1 : 0;
  }
  animateTo(scene, body, { scaleX: 1, scaleY: 1, alpha: 1 });
  animateTo(scene, backdrop, { alpha: 1 });
}

export function hideDialog(
  scene: Phaser.Scene,
  root: Phaser.GameObjects.Container,
  destroy = false
): void {
  if (root.getData('motion-open') === false) return;
  root.setData('motion-open', false);
  const body = root.getData('motion-body') as Phaser.GameObjects.Container | undefined;
  if (!body) {
    if (destroy) root.destroy(true);
    else root.setVisible(false);
    return;
  }
  const disabled: Phaser.GameObjects.GameObject[] = [];
  const disable = (child: Phaser.GameObjects.GameObject): void => {
    if (child.input?.enabled) {
      disabled.push(child);
      child.input.enabled = false;
    }
    if ('list' in child) (child as Phaser.GameObjects.Container).list.forEach(disable);
  };
  body.list.forEach(disable);
  root.setData('motion-disabled-inputs', disabled);
  const shade = root.list.find((child) => child !== body) as IMotionTarget;
  if (shade) animateTo(scene, shade, { alpha: 0 }, Motion.exitMs);
  animateTo(
    scene,
    body,
    {
      scaleX: reducedMotion() ? 1 : Motion.modalScale,
      scaleY: reducedMotion() ? 1 : Motion.modalScale,
      alpha: 0,
    },
    Motion.exitMs,
    Motion.ease,
    () => {
      if (root.getData('motion-open')) return;
      if (destroy) root.destroy(true);
      else root.setVisible(false);
    }
  );
}

/** 場景離開時解除系統偏好監聽。 */
export function watchReducedMotion(scene: Phaser.Scene, changed: () => void): void {
  media?.addEventListener('change', changed);
  scene.events.once('shutdown', () => media?.removeEventListener('change', changed));
}

export function celebrate(scene: Phaser.Scene, x: number, y: number): void {
  if (reducedMotion()) return;
  for (let i = 0; i < SPARK_COUNT; i++) {
    const angle = (i / SPARK_COUNT) * Math.PI * 2;
    const spark = scene.add.circle(x, y, SPARK_RADIUS, Motion.gold);
    animateTo(
      scene,
      spark,
      {
        x: x + Math.cos(angle) * SPARK_DISTANCE,
        y: y + Math.sin(angle) * SPARK_DISTANCE,
        alpha: 0,
        scaleX: 0,
        scaleY: 0,
      },
      SPARK_MS,
      Motion.ease,
      () => spark.destroy()
    );
  }
}

export function sweep(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  if (reducedMotion()) return;
  const light = scene.add.rectangle(
    x - width / 2 + SWEEP_WIDTH / 2,
    y,
    SWEEP_WIDTH,
    height,
    Motion.gold,
    SWEEP_ALPHA
  );
  animateTo(
    scene,
    light,
    { x: x + width / 2 - SWEEP_WIDTH / 2, alpha: 0 },
    SWEEP_MS,
    Motion.ease,
    () => light.destroy()
  );
  glow(scene, x, y, width, height, SWEEP_MS);
}
